import React from 'react';
import { Link } from 'react-router-dom';
import TabBarIcon from '../components/TabBarIcon';
import fleetlyLogo from '../../public/favicon.png';
import '../styles/EditCourse.css';

const Section = ({ icon, title, children }) => (
  <section style={{ marginBottom: '2rem' }}>
    <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#2c3e50', borderBottom: '2px solid #f0f0f0', paddingBottom: '8px' }}>
      <TabBarIcon name={icon} color="#4A90E2" />
      {title}
    </h2>
    {children}
  </section>
);

const Field = ({ name, desc }) => (
  <tr>
    <td style={{ fontWeight: 600, whiteSpace: 'nowrap', paddingRight: '16px', verticalAlign: 'top', paddingBottom: '6px' }}>{name}</td>
    <td style={{ color: '#555', paddingBottom: '6px' }}>{desc}</td>
  </tr>
);

function ManualPage() {
  return (
    <div className="edit-course-main-section">
      <div className="edit-course-header">
        <div className="course-context">
          <img src={fleetlyLogo} alt="Fleetly" style={{ height: '32px', width: 'auto' }} />
          <h3>Fleetly – User Manual</h3>
        </div>
        <div className="header-actions">
          <Link to="/" className="manual-link" title="Back to app">
            <TabBarIcon name="arrow-left" />
            <span>Back</span>
          </Link>
        </div>
      </div>

      <div style={{ maxWidth: '720px', lineHeight: '1.75', color: '#2c3e50', fontSize: '15px' }}>

        <Section icon="rocket" title="Getting Started">
          <ol style={{ paddingLeft: '1.4rem' }}>
            <li><strong>Allow location access</strong> when the browser prompts you. The app uses your GPS position to calculate where to place marks and to show your boat on the map. Without it you can still configure a course but cannot use "Set Start Position" or drop marks.</li>
            <li><strong>Configure the course</strong> using the form on the main page (see sections below).</li>
            <li>Press <strong>Lay The Course</strong>. The app calculates all mark positions and opens the map view.</li>
          </ol>
        </Section>

        <Section icon="ship" title="Fleet Configuration">
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <tbody>
              <Field name="Number of Boats" desc="How many boats are starting. Used to calculate the required start line length." />
              <Field name="Average Boat Length (m)" desc="Used together with the number of boats to size the start line." />
              <Field name="Start Line Scale Factor" desc="Multiplier applied to the calculated start line length. Default 1.3 adds a 30% safety margin." />
            </tbody>
          </table>
        </Section>

        <Section icon="wind" title="Wind & Course">
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <tbody>
              <Field name="Wind Direction (°)" desc="True wind direction in degrees (0 = North, 90 = East). Mark 1 is placed directly upwind of the start." />
              <Field name="Course Distance (m)" desc="Target windward leg length in metres." />
              <Field name="Course Type" desc="Shape of the course — see Course Types below." />
              <Field name="Finish Type" desc="Where the finish line is placed relative to the last mark. Options depend on the selected course type." />
              <Field name="Finish Distance (NM)" desc="Shown for finishes that are separate from a mark. Distance from the last mark to the finish line." />
              <Field name="Finish Angle (°)" desc="Bearing from the last mark to the finish line, for reaching finishes." />
            </tbody>
          </table>
        </Section>

        <Section icon="flag" title="Course Marks">
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <tbody>
              <Field name="Leeward Distance (NM)" desc="Distance from the start/finish area down to the leeward mark (Mark 4)." />
              <Field name="Windward Beats" desc="Number of upwind legs (2–4). More beats = more laps." />
              <Field name="Gate Width (# avg boat lengths)" desc="How wide gate marks are spread apart, expressed as a multiple of the average boat length." />
              <Field name="Gate 1 [1S/1P]" desc="Splits the windward mark into two separate port and starboard gate marks." />
              <Field name="Gate 3 [3S/3P]" desc="(Trapezoid only) Splits the middle windward mark into port and starboard gates." />
              <Field name="Gate 4 [4S/4P]" desc="Splits the leeward mark into port and starboard gates." />
            </tbody>
          </table>
        </Section>

        <Section icon="adjust" title="Offset Mark">
          <p>Only available when Gate 1 is <em>disabled</em>. An offset mark (1A) is placed near the windward mark to assist with rounding when the fleet is large.</p>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <tbody>
              <Field name="Offset Distance (NM)" desc="How far the offset mark sits from the windward mark." />
              <Field name="Offset Angle (°)" desc="Bearing from the windward mark to the offset mark." />
            </tbody>
          </table>
        </Section>

        <Section icon="map" title="Course Types">
          <p><strong>Leeward</strong> — Classic windward-leeward. Boats beat upwind to Mark 1, then run downwind to the leeward mark (Mark 4), and repeat. Simple and fast to set.</p>
          <p><strong>Windward</strong> — Same layout as Leeward but the finish is placed at the windward mark instead of the leeward end.</p>
          <p><strong>Inner Trapezoid</strong> — Adds a reaching leg inside the windward mark. Mark 2 is placed to one side at the top, creating a triangular loop before the downwind run. Suitable for spinnaker fleets.</p>
          <p><strong>Outer Trapezoid</strong> — Like the inner trapezoid but the reaching mark (Mark 2/3) is positioned outside the windward mark. Provides a longer, more open reach. The <em>Spinnaker</em> option switches reach angles between 70°/110° (no-kite) and 60°/120° (kite).</p>
        </Section>

        <Section icon="map-marker-alt" title="Map View">
          <p>After pressing <strong>Lay The Course</strong> the app switches to the map. All calculated mark positions are shown with their labels.</p>
          <ul style={{ paddingLeft: '1.4rem' }}>
            <li>Your <strong>live GPS position</strong> is shown as a moving dot on the map.</li>
            <li>Tap a mark label to select it, then use the <strong>Drop</strong> button in the toolbar to record the mark's actual position at your current GPS location. Tap <strong>Lift</strong> to reset it back to the ideal calculated position.</li>
            <li>Use the <strong>Fit</strong> button to zoom the map to show all marks.</li>
            <li>Use <strong>Back</strong> to return to the configuration screen without losing your settings.</li>
          </ul>
        </Section>

        <Section icon="globe" title="Sharing the Course">
          <p>The <strong>Share Link</strong> button copies a URL to your clipboard. The entire course configuration is encoded in the URL — no account or server needed.</p>
          <p>Send the link to the boat(s) laying the marks. When they open it:</p>
          <ul style={{ paddingLeft: '1.4rem' }}>
            <li>The course loads instantly with all the same settings.</li>
            <li>Their own <strong>GPS position</strong> is shown on the map.</li>
            <li>They can see exactly where each mark needs to be placed and sail to that position.</li>
            <li>Once on position, they select the mark and press <strong>Drop</strong> to record the actual placed position.</li>
          </ul>
          <p>The share link is available on both the configuration screen and the map view toolbar.</p>
        </Section>

      </div>
    </div>
  );
}

export default ManualPage;

